import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, X, Trash2, ChevronRight, ChevronDown, Eye, GitCompare } from "lucide-react";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Button } from "@/components/controls";
import { T } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import TeamCompareDrawer from "@/components/TeamCompareDrawer";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";

export default function Teams() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [fields, setFields] = useState({}); // module -> [field names]
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({}); // module -> bool (in the drawer)
  const [compareOpen, setCompareOpen] = useState(false);
  const { startPreview } = useAuth();
  const navigate = useNavigate();
  const pag = useClientPagination(rows, "teams");

  const load = async () => {
    try {
      const [t, m] = await Promise.all([api.get("/teams"), api.get("/teams/meta/modules")]);
      setRows(t.data);
      setModules(m.data.modules);
      setFields(m.data.fields || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/teams/${editing.id}`, editing);
      else await api.post("/teams", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete team?")) return;
    try { await api.delete(`/teams/${editing.id}`); toast.success("Deleted"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Feb 26 2026 — Edit implies View, and toggling View off cascades to Edit
  // off. Prevents saving the nonsense state where a field/module has
  // Edit:true / View:false (which used to render fields the user shouldn't
  // see; see the China Team regression on ~10 order fields).
  const togglePerm = (mod, kind) => setEditing((s) => {
    const perms = { ...(s.permissions || {}) };
    const cur = perms[mod] || { view: false, edit: false, delete: false, bulk_edit: false, fields: {} };
    const next = { ...cur, [kind]: !cur[kind] };
    // Cascade: bulk_edit → edit → view; delete → edit → view.
    // Turning edit off cascades DOWN (delete off, bulk_edit off).
    if ((kind === "delete" || kind === "bulk_edit") && next[kind] && !next.edit) next.edit = true;
    if ((kind === "delete" || kind === "bulk_edit") && next[kind] && !next.view) next.view = true;
    if (kind === "edit" && next.edit && !next.view) next.view = true;
    if (kind === "edit" && !next.edit) { next.delete = false; next.bulk_edit = false; }
    if (kind === "view" && !next.view) { next.edit = false; next.delete = false; next.bulk_edit = false; }
    perms[mod] = next;
    return { ...s, permissions: perms };
  });

  const toggleFieldPerm = (mod, field, kind) => setEditing((s) => {
    const perms = { ...(s.permissions || {}) };
    const cur = perms[mod] || { view: true, edit: false, fields: {} };
    const fmap = { ...(cur.fields || {}) };
    const fcur = fmap[field] || { view: cur.view, edit: cur.edit };
    const next = { ...fcur, [kind]: !fcur[kind] };
    if (kind === "edit" && next.edit && !next.view) next.view = true;
    if (kind === "view" && !next.view && next.edit) next.edit = false;
    fmap[field] = next;
    perms[mod] = { ...cur, fields: fmap };
    return { ...s, permissions: perms };
  });

  const resetFieldPerms = (mod) => setEditing((s) => {
    const perms = { ...(s.permissions || {}) };
    perms[mod] = { ...(perms[mod] || { view: true, edit: false }), fields: {} };
    return { ...s, permissions: perms };
  });

  return (
    <>
      <PageHeader title="Teams" subtitle="Role-based permissions matrix — expand a module to configure field-level access" testId={T.moduleTitle("teams")} module="teams">
        <Button onClick={() => setCompareOpen(true)} testId="teams-compare-btn" disabled={rows.length < 2}>
          <GitCompare size={13} /> Compare
        </Button>
        <Button variant="primary" onClick={() => setEditing({ name: "", permissions: {} })} testId={T.newBtn("teams")}>
          <Plus size={13} /> New Team
        </Button>
      </PageHeader>
      <PageBody>
        <div className="border border-slate-200 bg-white rounded-sm overflow-auto">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Team</th>
                {modules.map((m) => <th key={m} className="text-center capitalize">{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {pag.pageRows.map((t) => (
                <tr key={t.id} data-testid={T.row("teams", t.id)} onClick={() => { setEditing({ ...t, permissions: t.permissions || {} }); setExpanded({}); }} className="cursor-pointer">
                  <td className="font-medium">{t.name}</td>
                  {modules.map((m) => {
                    const p = t.permissions?.[m] || {};
                    const fieldsMap = p.fields || {};
                    const restricted = Object.keys(fieldsMap).length;
                    return (
                      <td key={m} className="text-center">
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono">
                          <span className={p.view ? "text-slate-700" : "text-slate-300"}>V</span>
                          <span className={p.edit ? "text-emerald-700 font-semibold" : "text-slate-300"}>E</span>
                          {restricted > 0 && <span className="text-blue-600" title={`${restricted} field-level overrides`}>·f{restricted}</span>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="teams" />
        </div>
      </PageBody>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setEditing(null)}>
          <div className="w-[760px] h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="font-heading text-[14px] font-semibold">{editing.id ? `Edit Team – ${editing.name}` : "New Team"}</div>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <Field label="Team Name">
                <Input testId={T.formInput("team-name")} value={editing.name || ""} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} />
              </Field>

              {/* --- Team-level access controls (Feb 2026) --- */}
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Team Access Controls</div>
                <div className="text-[11px] text-slate-500 mb-3">Applies to every user in this team unless the user overrides it individually.</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Login window START (IST, HH:MM)">
                    <Input testId={T.formInput("team-login-start")} placeholder="e.g. 09:00" value={editing.login_start_time || ""}
                           onChange={(e) => setEditing((s) => ({ ...s, login_start_time: e.target.value }))} />
                  </Field>
                  <Field label="Login window END (IST, HH:MM)">
                    <Input testId={T.formInput("team-login-end")} placeholder="e.g. 18:00" value={editing.login_end_time || ""}
                           onChange={(e) => setEditing((s) => ({ ...s, login_end_time: e.target.value }))} />
                  </Field>
                  <Field label="Max parallel sessions" span={2}>
                    <Input testId={T.formInput("team-max-parallel")} type="number" min="0" placeholder="0 = unlimited"
                           value={editing.max_parallel_sessions ?? ""}
                           onChange={(e) => setEditing((s) => ({ ...s, max_parallel_sessions: e.target.value === "" ? null : Number(e.target.value) }))} />
                  </Field>
                </div>
                <div className="mt-3">
                  <div className="field-label mb-1.5">Enabled Imports</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      // Feb 26 2026 — expanded to cover every module that has
                      // an import endpoint. Keys match the strings passed to
                      // `require_import_permission()` on the backend.
                      ["orders", "Marketplace Orders"],
                      ["sellerflex-orders", "Sellerflex Orders"],
                      ["flipkart-orders", "Flipkart Orders"],
                      ["marketplace-identifier", "Marketplace Identifier"],
                      ["shipping-update", "Shipping Update"],
                      ["sellerflex-shipping-update", "Sellerflex Shipping Update"],
                      ["order-status-update", "Order Status Update"],
                      ["procurement", "Procurement"],
                      ["accounts", "Accounts"],
                      ["mapping", "Mapping"],
                      ["inventory", "Inventory"],
                      ["warehouse", "Warehouse"],
                      ["customers", "Customers"],
                      ["bank", "Bank Transfers"],
                      ["catalog", "Catalog"],
                      ["asin_images", "ASIN Images"],
                      ["inbound", "Inbound"],
                    ].map(([key, label]) => {
                      const perms = editing.import_permissions || {};
                      return (
                        <label key={key} className="inline-flex items-center gap-2 text-[11.5px] py-0.5">
                          <input
                            type="checkbox"
                            data-testid={`team-import-${key}`}
                            checked={perms[key] === true}
                            onChange={(e) => setEditing((s) => ({
                              ...s,
                              import_permissions: { ...(s.import_permissions || {}), [key]: e.target.checked },
                            }))}
                          />
                          <span className="truncate">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <div className="field-label mb-2">Permissions</div>
                <div className="border border-slate-200 rounded-sm">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Module</th>
                        <th className="text-center">View</th>
                        <th className="text-center">Edit</th>
                        <th className="text-center">Delete</th>
                        <th className="text-center" title="Grants the Bulk Update bar for this module">Bulk Edit</th>
                        <th>Field overrides</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((m) => {
                        const mp = editing.permissions?.[m] || {};
                        const fmap = mp.fields || {};
                        const isOpen = expanded[m];
                        const hasFields = (fields[m] || []).length > 0;
                        return (
                          <React.Fragment key={m}>
                            <tr>
                              <td>
                                {hasFields && (
                                  <button
                                    data-testid={`expand-${m}`}
                                    onClick={() => setExpanded((s) => ({ ...s, [m]: !s[m] }))}
                                    className="text-slate-500 hover:text-slate-900"
                                  >
                                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                )}
                              </td>
                              <td className="capitalize">{m}</td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`perm-${m}-view`} checked={!!mp.view} onChange={() => togglePerm(m, "view")} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`perm-${m}-edit`} checked={!!mp.edit} onChange={() => togglePerm(m, "edit")} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`perm-${m}-delete`} checked={!!mp.delete} onChange={() => togglePerm(m, "delete")} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`perm-${m}-bulk_edit`}
                                  title="Grants the Bulk Update bar for this module"
                                  checked={!!mp.bulk_edit} onChange={() => togglePerm(m, "bulk_edit")} />
                              </td>
                              <td className="text-[11px] font-mono text-slate-500">
                                {Object.keys(fmap).length > 0 ? (
                                  <span>
                                    {Object.keys(fmap).length} custom
                                    <button onClick={() => resetFieldPerms(m)} className="ml-2 text-red-700 hover:underline">reset</button>
                                  </span>
                                ) : "—"}
                              </td>
                            </tr>
                            {isOpen && hasFields && (
                              <tr>
                                <td colSpan={7} className="bg-slate-50 p-0">
                                  <div className="p-3">
                                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Field-level overrides ({m})</div>
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                                      {(fields[m] || []).map((field) => {
                                        const fp = fmap[field] || { view: mp.view, edit: mp.edit };
                                        return (
                                          <div key={field} className="flex items-start gap-2 text-[12px] py-0.5">
                                            {/* Mar 4 2026 — full field name is now visible.
                                                Previously `truncate` chopped long procurement
                                                fields (e.g. `china_reseller_margin_percentage`).
                                                `break-all` + `leading-tight` lets the name wrap
                                                onto a second line inside the same 3-column grid
                                                cell, and the native `title` tooltip surfaces the
                                                exact name on hover for ops using screen readers /
                                                keyboard nav. */}
                                            <span
                                              className="flex-1 font-mono text-slate-700 break-all leading-tight"
                                              title={field}
                                            >{field}</span>
                                            <label className="inline-flex items-center gap-1">
                                              <input type="checkbox" data-testid={`fperm-${m}-${field}-view`} checked={!!fp.view} onChange={() => toggleFieldPerm(m, field, "view")} />
                                              <span className="text-slate-500 text-[10.5px]">V</span>
                                            </label>
                                            <label className="inline-flex items-center gap-1">
                                              <input type="checkbox" data-testid={`fperm-${m}-${field}-edit`} checked={!!fp.edit} onChange={() => toggleFieldPerm(m, field, "edit")} />
                                              <span className="text-slate-500 text-[10.5px]">E</span>
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="text-[11px] text-slate-500 font-mono mt-2">
                  V = View, E = Edit. Field overrides fall back to module-level when not set.
                </div>
              </div>
            </div>
            <div className="h-14 border-t border-slate-200 flex items-center justify-between px-4">
              <div className="flex gap-2">
                {editing.id && <Button variant="danger" onClick={del} testId={T.deleteBtn}><Trash2 size={13} /> Delete</Button>}
                {editing.id && (
                  <Button
                    onClick={() => {
                      startPreview({ id: editing.id, name: editing.name, permissions: editing.permissions || {} });
                      setEditing(null);
                      navigate("/");
                      toast.success(`Previewing as ${editing.name}`);
                    }}
                    testId="team-preview-btn"
                  >
                    <Eye size={13} /> Preview as team
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditing(null)} testId={T.cancelBtn}>Cancel</Button>
                <Button variant="primary" onClick={save} testId={T.saveBtn}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
